import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import {
  getDashboardBillingStatisticsQuery,
  getDashboardBarChartQuery,
  getDashboardICQuery,
  getCorrespondenceTableHashQuery,
} from "./queries";

import {
  DASHBOARD_ROLES,
  ROLE_GET_DASHBOARD_DIAGRAM_BAR,
} from "access-roles/roles";

import { EXPORT_BUTTONS, QUERY_STATUS } from "constants/constants";
import { useUserContext } from "api/user/UserProvider";
import { canUserPerformAction } from "access-roles/general-functions";
import { cleanUrl } from "utils/helper";
import { streamingDownload } from "utils/streamingDownload";

const DashboardContext = createContext({});

export const useDashboardContext = () => useContext(DashboardContext);

export const DashboardProvider = ({ ...props }) => {
  const [dashboardBillingStatistics, setDashboardBillingStatistics] = useState(
    {}
  );
  const [
    displayDashboardBillingStatisticsStatus,
    setDisplayDashboardBillingStatisticsStatus,
  ] = useState(QUERY_STATUS.INITIAL);
  const { userScope, userRoleList } = useUserContext();
  const [dashboardBarChart, setDashboardBarChart] = useState({});
  const [barChartStatus, setBarChartStatus] = useState(QUERY_STATUS.INITIAL);
  const [icDashboard, setIcDashboard] = useState([]);
  const [icStatus, setIcStatus] = useState(QUERY_STATUS.INITIAL);
  const [loadExportStatus, setLoadExportStatus] = useState(
    QUERY_STATUS.INITIAL
  );

  const fileExportSettings = {
    IC: {
      buildPath: ({ queryParams, lang }) =>
        `/dashboard/ic/export?scope=${userScope}&date=${queryParams.date}&lang=${lang}`,
      fileName: "intercontrat_export.xlsx",
    },
    BAR_CHART: {
      buildPath: ({ queryParams, lang }) =>
        `/dashboard/diagram/bar/export?${cleanUrl({
          scope: userScope,
          date: queryParams.date,
          mission_signed_by: queryParams.signed_by,
          mission_charged_by: queryParams.charged_by,
          mission_type: queryParams.mission_type,
          client: queryParams.client,
          lang,
        })}`,
      fileName: "bar_chart_export.xlsx",
    },
  };

  const getDashboardExportFile = useCallback(
    async ({ lang, exportFileType, queryParams }) => {
      const exportSetting = fileExportSettings[exportFileType];
      if (!exportSetting) return;
      try {
        setLoadExportStatus(QUERY_STATUS.LOADING);
        await streamingDownload({
          path: exportSetting.buildPath({ queryParams, lang }),
          fileName: exportSetting.fileName,
          mimeType:
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        setLoadExportStatus(QUERY_STATUS.SUCCESS);
      } catch (error) {
        setLoadExportStatus(QUERY_STATUS.ERROR);
        console.error(
          "Erreur lors de la génération ou du téléchargement du fichier:",
          error
        );
      }
    },
    [userScope]
  );

  const getDashboardIC = useCallback(
    async ({ page, date, signed_by }) => {
      if (
        !canUserPerformAction({
          userRoles: userRoleList,
          requiredRoles: ROLE_GET_DASHBOARD_DIAGRAM_BAR,
        })
      ) {
        return;
      }
      try {
        setIcStatus(QUERY_STATUS.LOADING);
        const result = await getDashboardICQuery({
          scope: userScope,
          page: page,
          date: date,
          signed_by: signed_by,
        });
        setIcDashboard(result);
        setIcStatus(QUERY_STATUS.SUCCESS);
      } catch (e) {
        setIcStatus(QUERY_STATUS.ERROR);
        console.error("getDashboardIC", e);
        throw e;
      }
    },
    [userScope, userRoleList]
  );

  const getDashboardBarChart = useCallback(
    async ({ page, date, signed_by, charged_by, mission_type, client }) => {
      if (
        !canUserPerformAction({
          userRoles: userRoleList,
          requiredRoles: ROLE_GET_DASHBOARD_DIAGRAM_BAR,
        })
      ) {
        return;
      }
      try {
        setBarChartStatus(QUERY_STATUS.LOADING);
        const result = await getDashboardBarChartQuery({
          scope: userScope,
          page: page,
          per_page: 15,
          date: date,
          signed_by,
          charged_by,
          mission_type,
          client,
        });
        setDashboardBarChart(result);
        setBarChartStatus(QUERY_STATUS.SUCCESS);
      } catch (e) {
        console.error("getDashboardBarChart", e);
        setBarChartStatus(QUERY_STATUS.ERROR);
        throw e;
      }
    },
    [userScope, userRoleList]
  );

  const dashboardBillingStatisticsGetter = useCallback(
    async ({ scope, year }) => {
      if (
        !canUserPerformAction({
          userRoles: userRoleList,
          requiredRoles: DASHBOARD_ROLES.ROLE_GET_DASHBOARD_BILLING_STATS,
        })
      ) {
        return;
      }
      try {
        const result = await getDashboardBillingStatisticsQuery({
          scope,
          year,
        });
        setDashboardBillingStatistics(result);
        setDisplayDashboardBillingStatisticsStatus(QUERY_STATUS.SUCCESS);
      } catch (e) {
        console.error("dashboardBillingStatisticsGetter", e);
        setDisplayDashboardBillingStatisticsStatus(QUERY_STATUS.ERROR);
        throw e;
      }
    },
    [setDashboardBillingStatistics, userRoleList]
  );

  const downloadCorrespondenceTableExport = useCallback(
    async ({ exportType, csltStrFilter, startDateFilter, endDateFilter }) => {
      const isExportCa = exportType === EXPORT_BUTTONS.CA;

      try {
        const hash = await getCorrespondenceTableHashQuery({
          exportCa: isExportCa,
          workerType: csltStrFilter,
          startDate: startDateFilter,
          endDate: endDateFilter,
          scope: userScope,
        });
        await streamingDownload({
          path: `/file/${hash.uri}`,
          fileName: `export_${isExportCa ? "ca" : "correspondence"}.xlsx`,
          mimeType:
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
      } catch (error) {
        console.error(
          "Erreur lors de la génération ou du téléchargement du fichier :",
          error
        );
        throw error;
      }
    },
    [userScope]
  );

  const values = useMemo(
    () => ({
      dashboardBillingStatisticsGetter,
      dashboardBillingStatistics,
      setDashboardBillingStatistics,
      displayDashboardBillingStatisticsStatus,
      getDashboardBarChart,
      dashboardBarChart,
      barChartStatus,
      getDashboardIC,
      icDashboard,
      setIcDashboard,
      getDashboardExportFile,
      userScope,
      icStatus,
      loadExportStatus,
      setLoadExportStatus,
      downloadCorrespondenceTableExport,
    }),
    [
      icStatus,
      userScope,
      getDashboardIC,
      icDashboard,
      barChartStatus,
      dashboardBarChart,
      getDashboardBarChart,
      dashboardBillingStatisticsGetter,
      dashboardBillingStatistics,
      setDashboardBillingStatistics,
      displayDashboardBillingStatisticsStatus,
      getDashboardExportFile,
      loadExportStatus,
      downloadCorrespondenceTableExport,
    ]
  );
  return <DashboardContext.Provider value={values} {...props} />;
};
